import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Receipt,
  Clock,
  User,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { useReceiptPrint } from '@/components/orders/ReceiptPrintView';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { toast } from 'sonner';

interface OrderForReprint {
  id: string;
  order_number: string;
  order_type: string;
  status: string;
  total: number;
  created_at: string;
  customer?: { first_name: string | null; last_name: string | null } | null;
  server?: { first_name: string | null; last_name: string | null } | null;
  table?: { name: string } | null;
  items?: Array<{
    id: string;
    name: string;
    quantity: number;
    unit_price: number;
    total: number;
    notes?: string | null;
    modifiers?: unknown;
    status: string;
  }>;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  tip_amount: number;
}

interface ReceiptReprintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper function to format time
const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export function ReceiptReprintDialog({
  open,
  onOpenChange,
}: ReceiptReprintDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<OrderForReprint | null>(null);
  const [showPinDialog, setShowPinDialog] = useState(false);

  const { currentLocation } = useLocationStore();
  const { profile } = useAuth();
  const { formatCurrency } = useCurrency();
  const { printReceipt } = useReceiptPrint();

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedOrder(null);
      setShowPinDialog(false);
    }
  }, [open]);

  // Fetch recent orders for the location (last 50 completed/open orders)
  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders-for-reprint', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          order_type,
          status,
          total,
          subtotal,
          tax_amount,
          discount_amount,
          tip_amount,
          created_at,
          customer:customers(first_name, last_name),
          server:profiles!orders_user_id_fkey(first_name, last_name),
          table:tables(name),
          items:order_items(id, name, quantity, unit_price, total, notes, modifiers, status)
        `)
        .eq('location_id', currentLocation.id)
        .in('status', ['open', 'sent', 'in_progress', 'ready', 'completed'])
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      // Transform the data to match our interface (Supabase returns arrays for joins)
      return (data || []).map((order: any) => ({
        ...order,
        customer: Array.isArray(order.customer) ? order.customer[0] : order.customer,
        server: Array.isArray(order.server) ? order.server[0] : order.server,
        table: Array.isArray(order.table) ? order.table[0] : order.table,
      })) as OrderForReprint[];
    },
    enabled: open && !!currentLocation?.id,
  });

  // Filter orders based on search query
  const filteredOrders = orders?.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const customerName = order.customer
      ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.toLowerCase()
      : '';
    return (
      order.order_number.toLowerCase().includes(query) ||
      customerName.includes(query)
    );
  }) ?? [];

  // Handle order selection - always requires PIN for cashiers
  const handleSelectOrder = (order: OrderForReprint) => {
    setSelectedOrder(order);
    setShowPinDialog(true);
  };

  // Handle PIN verification success - proceed with printing
  const handlePinVerified = async (managerId: string, _managerName: string) => {
    if (!selectedOrder) return;

    // Get reprint count for this order
    const { data: prints } = await supabase
      .from('receipt_prints')
      .select('id, print_type')
      .eq('order_id', selectedOrder.id);

    const reprintCount = prints?.filter(p => p.print_type === 'reprint').length ?? 0;
    const hasOriginalPrint = prints?.some(p => p.print_type === 'original') ?? false;
    const isReprint = hasOriginalPrint;
    const printNumber = isReprint ? reprintCount + 1 : undefined;

    // Print the receipt
    const success = await printReceipt({
      order: selectedOrder as any,
      location: currentLocation ? {
        name: currentLocation.name,
        phone: currentLocation.phone,
      } : null,
      isReprint,
      printNumber,
    });

    if (success) {
      // Log the print with supervisor ID who approved
      if (currentLocation && profile) {
        await supabase
          .from('receipt_prints')
          .insert({
            order_id: selectedOrder.id,
            location_id: currentLocation.id,
            user_id: profile.id,
            print_type: isReprint ? 'reprint' : 'original',
            approved_by: managerId, // Store supervisor's UUID for audit trail
          });
      }

      toast.success('Receipt printed', {
        description: `Receipt for order #${selectedOrder.order_number} has been sent to the printer.`,
      });
    } else {
      toast.error('Print failed', {
        description: 'Could not open print dialog. Please check your browser settings.',
      });
    }

    setShowPinDialog(false);
    setSelectedOrder(null);
    onOpenChange(false);
  };

  const orderTypeLabels: Record<string, string> = {
    dine_in: 'Dine-in',
    takeout: 'Takeout',
    delivery: 'Delivery',
    bar_tab: 'Bar Tab',
    online: 'Online',
  };

  return (
    <>
      <ResponsiveModal open={open && !showPinDialog} onOpenChange={onOpenChange}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-2">
              <Receipt className="h-6 w-6 text-blue-600" />
            </div>
            <ResponsiveModalTitle className="text-center">Reprint Receipt</ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center">
              Select an order to reprint its receipt. Manager approval required.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order # or customer name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Orders List */}
            <div className="h-[400px] overflow-y-auto pr-2">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <span className="text-sm">Loading orders...</span>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <span className="text-sm">
                    {searchQuery ? 'No orders match your search' : 'No recent orders found'}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredOrders.map((order) => {
                    const customerName = order.customer
                      ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
                      : null;

                    return (
                      <Button
                        key={order.id}
                        variant="outline"
                        className={cn(
                          'w-full h-auto p-4 flex flex-col items-stretch gap-2 text-left',
                          'hover:bg-accent/50 transition-colors'
                        )}
                        onClick={() => handleSelectOrder(order)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-base">
                              #{order.order_number}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {orderTypeLabels[order.order_type] || order.order_type}
                            </Badge>
                            {order.table?.name && (
                              <Badge variant="outline" className="text-xs">
                                Table {order.table.name}
                              </Badge>
                            )}
                          </div>
                          <span className="font-semibold">
                            {formatCurrency(order.total ?? 0)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              <span>{formatDate(order.created_at)} at {formatTime(order.created_at)}</span>
                            </div>
                            {customerName && (
                              <div className="flex items-center gap-1">
                                <User className="h-3.5 w-3.5" />
                                <span>{customerName}</span>
                              </div>
                            )}
                          </div>
                          <Badge
                            variant="secondary"
                            className={cn(
                              'text-xs',
                              order.status === 'completed' && 'bg-gray-500/10 text-gray-600',
                              order.status === 'open' && 'bg-blue-500/10 text-blue-600',
                              order.status === 'sent' && 'bg-yellow-500/10 text-yellow-600',
                              order.status === 'in_progress' && 'bg-blue-500/10 text-blue-600',
                              order.status === 'ready' && 'bg-green-500/10 text-green-600'
                            )}
                          >
                            {order.status === 'in_progress' ? 'Preparing' :
                             order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </ResponsiveModalBody>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Manager PIN Dialog */}
      <ManagerPinDialog
        open={showPinDialog}
        onOpenChange={(open) => {
          setShowPinDialog(open);
          if (!open) {
            setSelectedOrder(null);
          }
        }}
        onVerified={handlePinVerified}
        title="Supervisor Approval Required"
        description={`Enter supervisor PIN to reprint receipt for order #${selectedOrder?.order_number || ''}`}
      />
    </>
  );
}
